import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Manager, Popper, PopperProps, Reference, ReferenceChildrenProps } from 'react-popper';
import { JSXUnrendered, UnsafeSpreadProps, useOverlayMountNode } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { css } from '@tanium/style-system';
import { ThemeZIndices } from '@tanium/theme-data';
import { type MenuGroupProps } from './MenuGroup';
import { MenuItemProps, StyledMenuItem } from './MenuItem';
import { ContainerBorder, ContainerFill, ContainerShadow } from './themeDefinitions';

type MenuContent =
  | React.ReactElement<MenuItemProps>
  | React.ReactElement<MenuGroupProps>
  | JSXUnrendered;

export type MenuSize = 'sm' | 'lg';

export interface MenuProps<T = unknown> extends UnsafeSpreadProps {
  /**
   * Render prop for the component which will receive the menu.
   */
  children: (args: AnchorProps) => React.ReactNode;
  /**
   * Content to display in the menu.
   */
  content: MenuContent | MenuContent[];
  /**
   * The placement of the menu. This follows popper's conventions for placement (e.g.
   * 'bottom-start', 'right-end').
   */
  placement?: PopperProps<T>['placement'];
  /**
   * Modifiers for the underlying popper implementation. This can be used to correct flipping
   * behavior, fine-tune offsets, and more.
   */
  modifiers?: PopperProps<T>['modifiers'];
  /**
   * Callback fired when the menu is hidden
   */
  onHide?: () => void;
  /**
   * Callback fired when the menu is shown
   */
  onShow?: () => void;
  /**
   * The size of the menu
   */
  size?: MenuSize;
}

export interface AnchorProps {
  /**
   * Ref to be passed to the component receiving a menu. This is where the menu gets anchored. Note
   * that functional components will need to use React.forwardRef
   * (https://reactjs.org/docs/forwarding-refs.html).
   */
  ref: ReferenceChildrenProps['ref'];
  /**
   * Function to display the menu.
   */
  show: () => void;
  /**
   * Function for hiding the menu. Global clicks collapse the menu without any further action.
   * Typically consumers do not need to invoke this function.
   */
  hide: () => void;
}

export interface MenuState {
  isShown: boolean;
}

export const StyledMenu = styled('ul', {
  shouldForwardProp: (p) => p !== 'size',
})<Pick<MenuProps, 'size'>>(
  css({
    // for popper - "CSS "margin" styles cannot be used to apply padding between the popper and its reference element or boundary
    m: 0,
    boxSizing: 'border-box',
    border: 1,
    borderColor: ContainerBorder,
    borderRadius: 1,
    backgroundColor: ContainerFill,
    flexDirection: 'column',
    padding: 0,
    boxShadow: ContainerShadow,
    zIndex: ThemeZIndices.Popover,
  }),
  ({ size }) =>
    css({
      [`${StyledMenuItem}`]: {
        ...(size === 'sm'
          ? {
              fontSize: '12px',
              height: 3,
            }
          : {
              fontSize: '14px',
              height: 4,
            }),
        ':first-of-type': { borderTopLeftRadius: 1, borderTopRightRadius: 1 },
        ':last-of-type': { borderBottomLeftRadius: 1, borderBottomRightRadius: 1 },
      },
    }),
);

const defaultModifiers: MenuProps['modifiers'] = [];

/**
 * Used to attach a menu to an arbitrary child component. The menu adds a global handler to the
 * document to detect clicks outside the component (so that the menu will be hidden upon clicking
 * outside the menu).
 */
export const Menu = ({
  children,
  content,
  modifiers = defaultModifiers,
  onHide,
  onShow,
  size = 'sm',
  placement = 'bottom-start',
  __UNSAFE_ADDITIONAL_PROPS__,
}: MenuProps) => {
  const [isShown, setIsShown] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);

  // FIXME: UX-4149 if `show` is used as say a click event handler on a menu trigger,
  // it will actually toggle the menu open and closed and `onShow` gets called
  // on every open/close. `onHide` behaves as advertised and is only called when
  // menu is hidden.
  const show = useCallback(() => {
    if (onShow) {
      onShow();
    }

    if (isShown && onHide) {
      onHide();
    }

    setIsShown((wasShown) => !wasShown);
  }, [isShown, onHide, onShow]);

  const hide = useCallback(() => {
    if (onHide) {
      onHide();
    }
    setIsShown(false);
  }, [onHide]);

  useEffect(() => {
    const handleClickOutsideTrigger = (e: MouseEvent) => {
      // Ignore clicks on trigger to avoid always hiding the menu
      // after clicking (an issue that surfaces when using React 17+)
      if (triggerRef.current && triggerRef.current.contains(e.target as HTMLElement)) {
        return;
      }

      hide();
    };

    if (isShown) {
      document.addEventListener('click', handleClickOutsideTrigger);

      return () => {
        document.removeEventListener('click', handleClickOutsideTrigger);
      };
    }

    return undefined;
  }, [hide, isShown]);

  const mountNode = useOverlayMountNode();

  const shouldRenderContent = !!(isShown && content && (!Array.isArray(content) || content.length));

  return (
    <Manager>
      <Reference innerRef={triggerRef}>{({ ref }) => children({ ref, show, hide })}</Reference>
      {shouldRenderContent &&
        ReactDOM.createPortal(
          <Popper placement={placement} modifiers={modifiers} {...__UNSAFE_ADDITIONAL_PROPS__}>
            {({ ref, style }) => (
              <StyledMenu size={size} style={style} ref={ref}>
                {content}
              </StyledMenu>
            )}
          </Popper>,
          mountNode,
        )}
    </Manager>
  );
};
